import { AuthProps } from '../../types/auth/auth';
import { UserProfile } from '../../types/auth/user';
import { OrganisationResponse } from '../../types/organisations/organisation';

const TOKEN_KEY = 'nuxeoToken';
const REFRESH_TOKEN_KEY = 'nuxeoRefreshToken';
const USER_KEY = 'nuxeoUser';
const ORGANISATION_KEY = 'nuxeoOrganisation';
const PACKAGE_KEY = 'nuxeoPackage';

const hasWindow = () => typeof window !== 'undefined';

const safeJsonParse = <T>(value: string | null): T | null => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

export const buildUserProfile = (authData: AuthProps): UserProfile => ({
  id: authData.id ?? null,
  company: authData.company,
  name: authData.name,
  surname: authData.surname,
  username: authData.username,
  email: authData.email || authData.username,
  role: authData.role ?? null,
});

export const authStorage = {
  getToken(): string | null {
    return hasWindow() ? localStorage.getItem(TOKEN_KEY) : null;
  },

  getRefreshToken(): string | null {
    return hasWindow() ? localStorage.getItem(REFRESH_TOKEN_KEY) : null;
  },

  getUser(): UserProfile | null {
    return hasWindow() ? safeJsonParse<UserProfile>(localStorage.getItem(USER_KEY)) : null;
  },

  getOrganisation(): OrganisationResponse | null {
    return hasWindow()
      ? safeJsonParse<OrganisationResponse>(localStorage.getItem(ORGANISATION_KEY))
      : null;
  },

  setAuth(authData: AuthProps) {
    if (!hasWindow()) {
      return;
    }

    const savedUser = authStorage.getUser();
    const userProfile = buildUserProfile(authData);

    localStorage.setItem(TOKEN_KEY, authData.token);
    localStorage.setItem(REFRESH_TOKEN_KEY, authData.refreshToken);
    localStorage.setItem(
      USER_KEY,
      JSON.stringify({
        ...savedUser,
        ...userProfile,
        id: userProfile.id ?? savedUser?.id ?? null,
        role: userProfile.role ?? savedUser?.role ?? null,
      } satisfies UserProfile),
    );
  },

  setUser(user: UserProfile) {
    if (!hasWindow()) {
      return;
    }

    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  setOrganisation(organisation: OrganisationResponse | null) {
    if (!hasWindow()) {
      return;
    }

    if (!organisation) {
      localStorage.removeItem(ORGANISATION_KEY);
      return;
    }

    localStorage.setItem(ORGANISATION_KEY, JSON.stringify(organisation));
  },

  clearAuth() {
    if (!hasWindow()) {
      return;
    }

    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ORGANISATION_KEY);
    localStorage.removeItem(PACKAGE_KEY);
  },
};
